import clsx from "clsx";
import { Button, ButtonVariant, getButtonStyle } from "components/Form/Button";
import { Layout } from "components/Layout";
import {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { AppRoutes } from "types";
import { Explanation, ExplanationIcons } from "components/Explanation";
import { GalleryPreview } from "components/GalleryPreview";
import { UploadContext, UploadType } from "contexts/UploadContext";
import { usePrivacyModal } from "hooks/usePrivacyModal";
import { CollectionDetailsContext } from "contexts/CollectionDetails";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { getAcceptedFileTypes } from "lib/upload-files";

export const PcrCapture = function PcrCapture() {
  const [init, setInit] = useState<boolean>(false);
  const [hasDuplicates, setHasDuplicates] = useState<boolean>(false);

  const {
    files: images,
    setFiles: setImages,
    resetFiles: resetImages,
    upload,
    checkForDuplicates,
  } = useContext(UploadContext);
  const { config } = useContext(CollectionDetailsContext);
  const privacyModal = usePrivacyModal();
  const navigate = useNavigate();
  const location = useLocation();

  const taskId = location.state?.taskId;

  const handleChangeInput = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      if (!event.target.files) {
        return;
      }

      if (event.target.files) {
        setImages(Array.from(event.target.files));
      }
    },
    [setImages]
  );

  useEffect(() => {
    if (!config || privacyModal.accepted === null) {
      return;
    }

    if (privacyModal.accepted === false) {
      navigate(AppRoutes.home);
      return;
    }

    if (!taskId) {
      navigate(AppRoutes.home);
      return;
    }

    setInit(true);
  }, [config, navigate, privacyModal.accepted, taskId]);

  // Check for duplicates when images change (preview screen only)
  useEffect(() => {
    if (images.length > 0) {
      const duplicates = checkForDuplicates();
      const hasDups = duplicates.length > 0;
      setHasDuplicates(hasDups);

      if (hasDups) {
        toast.warn(`Some files were not added due to errors: duplication.`);
      }
    } else {
      setHasDuplicates(false);
    }
  }, [images, checkForDuplicates]);

  const handleUpload = () => {
    if (!taskId) {
      toast.error("Cannot upload: No task selected");
      return;
    }
    upload(UploadType.PCR, taskId);
  };

  if (!init) {
    return null;
  }

  return (
    <Layout
      className="space-y-8"
      toolbarClassName="z-[100] !py-3"
      toolbar={
        <>
          {images.length <= 0 && (
            <>
              <label className={clsx(getButtonStyle(), "cursor-pointer")}>
                <input
                  type="file"
                  accept={getAcceptedFileTypes("pcr")}
                  multiple
                  onChange={handleChangeInput}
                  hidden
                />
                <span>Choose files</span>
              </label>
              <button
                onClick={() => navigate(AppRoutes.home)}
                className="text-sm text-858494 underline hover:text-191c20 transition-colors"
              >
                Back to Home
              </button>
            </>
          )}
          {images.length > 0 && (
            <>
              <Button onClick={handleUpload} disabled={hasDuplicates}>
                Upload
              </Button>
              <Button onClick={resetImages} variant={ButtonVariant.FLAT}>
                Choose another
              </Button>
            </>
          )}
        </>
      }
    >
      <h1 className="text-24 font-semibold text-center">
        {images.length <= 0 && "PCR Capture"}
        {images.length > 0 && "Preview PCR file"}
      </h1>

      {images.length > 0 && (
        <div className="text-858494 leading-5 text-center">
          Review images, videos, and labels carefully. <br />
          Correct any mismatches before uploading.
        </div>
      )}

      {images.length <= 0 && (
        <Explanation
          items={[
            {
              icon: ExplanationIcons.note,
              title: "Input collection details",
              description: (
                <p>
                  Add any relevant information you want to upload along with the
                  files. Ensure all details entered apply consistently across
                  all files uploaded in the session.
                </p>
              ),
            },
            {
              icon: ExplanationIcons.selfie,
              title: "Select files to upload",
              description: (
                <p>
                  Tap on the button to select images and/or video files you
                  intend to upload. Preview them and confirm the upload
                </p>
              ),
            },
            {
              icon: ExplanationIcons.bulb,
              title: "Requirements",
              description: (
                <ul className="leading-[18px] list-disc">
                  <li>Ensure all uploads are in portrait orientation.</li>
                  <li>
                    Videos must remain in portrait mode throughout their
                    duration.
                  </li>
                  <li>Limit video length to 15 seconds max.</li>
                  <li>
                    File sizes should be at least 10 KB and no more than 25 MB
                    for images, and up to 250 MB for videos.
                  </li>
                  <li>
                    ZIP files for PCR uploads must be between 10 KB and 1 GB.
                  </li>
                </ul>
              ),
            },
          ]}
        />
      )}
      {images.length > 0 && (
        <GalleryPreview
          isRemovable
          showLabels={
            config?.labels &&
            Object.keys(config.labels).some((key) => key !== "devices")
          }
        />
      )}
    </Layout>
  );
};
